'use client'
import type { Optional, PartiallyOptional } from '@cdc-internal/fe-toolbox/type-utils'
import { memoize } from 'es-toolkit/function'

import BaseIcon, { type NextBaseIconProps } from '@/components/basic/BaseIcon'
import { logger } from '@/configs'
import useGlobalStore from '@/stores/global'
import type { CoinFromAll, CoinId, CoinOverall, CoinSlug } from '@/types/coin'
import { buildCoinIconUrlBySourceSlug } from '@/utils/url'

type SimpleCoinMeta = Optional<
  Pick<CoinOverall, 'id' | 'token_id' | 'symbol' | 'slug' | 'source_slug' | 'icon' | 'name'> &
    Pick<CoinFromAll, 'source_id'>
>

const defaultCoinSize = 20

interface Props extends PartiallyOptional<NextBaseIconProps, 'alt' | 'size'> {
  coin: SimpleCoinMeta
  // Use default icon source composed of source slug - https://${STATIC_DOMAIN}/token/icons/${source_slug}/color_icon.png
  useSlugIconSource?: boolean
  fallbackLetter?: string
}

// Able to show loading background and show a fallback letter (first letter of the coin symbol) when the icon fails to load
export default function CoinIcon({
  coin,
  alt,
  useSlugIconSource,
  size,
  fallbackLetter,
  ...restProps
}: Props) {
  const allCoinsStatus = useGlobalStore(state => state.allCoinsStatus)

  const { id, token_id, source_id, symbol, slug, source_slug, icon, name } = coin ?? {}
  // token_id should be the real internal coin id if it exists
  const internalCoinId = token_id || id || source_id

  // Not every token entity has source_slug or icon, so find it by token id or slug instead if it's not provided.
  let iconSource = useGetCoinValue(
    'icon',
    icon,
    internalCoinId,
    // Find the specific token by slug or source_slug
    slug || source_slug,
  )

  const iconSlug = useGetCoinValue('source_slug', source_slug, internalCoinId, slug) || slug

  // icon with null value means using default icon source
  if ((useSlugIconSource || iconSource === null) && iconSlug) {
    iconSource = buildCoinIconUrlBySourceSlug(iconSlug)
  }

  const fallbackText = (fallbackLetter || name || symbol || slug || source_slug || '')[0]

  const sourcePending = allCoinsStatus === 'pending' || allCoinsStatus === undefined

  return (
    <BaseIcon
      src={iconSource ?? undefined}
      sourcePending={sourcePending}
      size={size || defaultCoinSize}
      fallbackText={fallbackText}
      alt={alt || symbol || 'icon'}
      circle
      {...restProps}
    />
  )
}

type SearchCoinKeys = 'icon' | 'source_slug'

const memoizedFindValueByCoinId = memoize(
  ([id, key]: [id: CoinId, key: SearchCoinKeys]) =>
    useGlobalStore.getState().allCoins.find(coin => coin.id === id)?.[key],
  {
    getCacheKey: args => [...args, useGlobalStore.getState().allCoins.length].join('.'),
  },
)

const memoizedFindValueByCoinSlug = memoize(
  ([slug, key]: [slug: CoinSlug | undefined, key: SearchCoinKeys]) =>
    useGlobalStore
      .getState()
      .allCoins.find(coin => coin.slug === slug || coin.source_slug === slug)?.[key],
  {
    getCacheKey: args => [...args, useGlobalStore.getState().allCoins.length].join('.'),
  },
)

function useGetCoinValue(
  key: SearchCoinKeys,
  originalValue?: string | null,
  id?: CoinId,
  slug?: CoinSlug,
) {
  const allCoins = useGlobalStore(state => state.allCoins)

  // Empty icon string means manually removed
  if (originalValue || originalValue === '') return originalValue
  if (!id && !slug) return undefined

  // Find the specific coin value only after all coins are ready
  if (!allCoins.length) return undefined

  try {
    return id ? memoizedFindValueByCoinId([id, key]) : memoizedFindValueByCoinSlug([slug, key])
  } catch (error) {
    logger.warn('Error finding coin value:', error)
    return undefined
  }
}
